import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import useStore from '../../store/useStore';

// Right-hand side drawer used for invite, role editing and organisation details.
const Drawer = ({ open, kicker, title, subtitle, onClose, footer, children }) => {
  const theme = useStore(s => s.theme);
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    panelRef.current?.focus();
    const onKey = (e) => {
      // Let a nested ConfirmModal handle its own Escape first.
      if (e.key === 'Escape' && !e.defaultPrevented) onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: theme === 'light' ? 'rgba(20,20,20,0.35)' : 'rgba(0,0,0,0.6)' }}
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="absolute right-0 top-0 h-full w-full max-w-[540px] flex flex-col border-l outline-none"
        style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}
      >
        <span aria-hidden="true" className="absolute -left-px top-8 w-px h-[72px]" style={{ background: 'var(--accent)' }} />
        <div className="flex items-start justify-between gap-4 px-8 pt-7 pb-5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
          <div className="flex flex-col gap-1.5 min-w-0">
            {kicker && (
              <span className="font-mono text-[11px] tracking-[0.14em]" style={{ color: 'var(--ink-link)' }}>{kicker}</span>
            )}
            <h2 className="m-0 font-heading text-2xl font-semibold tracking-[-0.01em] break-words">{title}</h2>
            {subtitle && <p className="m-0 text-[13.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-10 h-10 shrink-0 flex items-center justify-center rounded-[10px] border"
            style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-8 py-6">{children}</div>
        {footer && (
          <div className="flex items-center justify-between gap-3 px-8 py-5 border-t" style={{ borderColor: 'var(--surf-line)' }}>
            {footer}
          </div>
        )}
      </aside>
    </div>
  );
};

export default Drawer;
